import React, { useMemo, useEffect } from 'react';
import { Box } from '@mui/system';
import { useQueryClient } from '@tanstack/react-query';
import { Loading, MultiSelectControlledOption } from 'components';
import { getFiscalYear } from 'utils';
import { useScorecardDataBySite, useNotify, useGetConfigParameter, useSelectedSite } from 'hooks';
import { QUERY_KEYS } from 'consts';
import { ScorecardMainTable, ArchivedDataTable } from './components';

interface FilterValues {
    selectedFiscalYears: MultiSelectControlledOption[];
    selectedToggleOption: 'INPUT_UNIT' | 'DEFAULT_UNIT';
}

interface ScorecardContentProps {
    selectedPeriod: string;
    filterValues: FilterValues;
    isAdmin: boolean;
    isEditMode: boolean;
    userRoles: string[];
}

export const ScorecardContent = ({ selectedPeriod, filterValues, isAdmin, isEditMode, userRoles }: ScorecardContentProps) => {
    const selectedSite = useSelectedSite();
    const siteId = selectedSite?.id;
    const notify = useNotify();
    const queryClient = useQueryClient();
    const { data: configParameter } = useGetConfigParameter();
    const { selectedFiscalYears, selectedToggleOption } = filterValues;

    const companyMonths = useMemo(() => configParameter
        ?.filter((param: any) => param.name.includes("company_month"))
        .map((param: any) => param.configValue) ?? ["Jan", "Dec"]
        , [configParameter]);

    // Convert fiscal years to date ranges
    const dateRanges = useMemo(() => {
        if (!selectedFiscalYears.length || !companyMonths) return [];

        return selectedFiscalYears.map(fiscalYear => {
            const year = parseInt(fiscalYear.value.toString().split(' ')[0], 10);
            const fiscalYearData = getFiscalYear(companyMonths, `${year} Fiscal Year`);
            return {
                startDate: fiscalYearData.fromDate,
                endDate: fiscalYearData.endDate,
                fiscalYear: year
            };
        });
    }, [selectedFiscalYears, companyMonths]);

    // Memoize the query parameters to prevent unnecessary re-renders
    const queryParams = useMemo(() => ({
        siteId: siteId || 1,
        dateRanges,
        timeframe: selectedPeriod.toUpperCase(),
        unit: selectedToggleOption
    }), [siteId, dateRanges, selectedPeriod, selectedToggleOption]);

    // Use hook to get period and site specific data
    const { data: scorecardData, isLoading, isFetching, error } = useScorecardDataBySite(queryParams, !!siteId && selectedFiscalYears.length > 0);

    // Filter data based on archived status
    const { activeData, archivedData } = useMemo(() => {
        if (!scorecardData) {
            return { activeData: [], archivedData: [] };
        }

        const active = scorecardData.filter(item => !item.archived);
        const archived = scorecardData.filter(item => item.archived);

        return { activeData: active, archivedData: archived };
    }, [scorecardData]);

    // Handle error with useEffect to prevent unnecessary re-renders
    useEffect(() => {
        if (error) {
            notify.error('Error loading scorecard data');
        }
    }, [error, notify]);

    const handleDataUpdate = () => {
        // Invalidate and refetch scorecard data
        queryClient.invalidateQueries([QUERY_KEYS.SCORECARD_DATA]);
    };

    if (isLoading || isFetching) {
        return (
            <Box sx={{ display: 'flex', height: '150px', position: 'relative', padding: '24px 41px' }}>
                <Loading />
            </Box>
        );
    }

    return (
        <Box sx={{ marginTop: '24px' }}>
            <ScorecardMainTable
                data={activeData}
                isEditMode={isEditMode}
                selectedPeriod={selectedPeriod}
                dateRanges={dateRanges}
                isAdmin={isAdmin}
                selectedToggleOption={selectedToggleOption}
                onDataUpdate={handleDataUpdate}
                userRoles={userRoles}
            />
            <ArchivedDataTable
                archivedData={archivedData}
                selectedPeriod={selectedPeriod}
                dateRanges={dateRanges}
                isAdmin={isAdmin}
                selectedUnit={selectedToggleOption}
                isEditModeEnabled={isEditMode}
                userRoles={userRoles}
            />

        </Box>
    );
}; 